import type { UploadRule, UploadRuleCheck } from '@erp/rpc';

import { columnIndex, type UploadLine } from './resultsToUpload';

/**
 * Runs the org's upload validation rules against the lines about to be
 * written to the CSV — after the mapping and the calculated columns, so a rule
 * sees exactly what AS400 will.
 *
 * A blank value only fails a `required` check; every other check judges values
 * that are present. That keeps "CC must be under 5" from flagging every row
 * without a core cost adder — pair it with `required` to demand the value.
 */

export interface RuleFailure {
  /** The line's position in the upload CSV, 1 to N. */
  line: number;
  /** Excel row number in the supplier's workbook. */
  sheetRow: number;
  value: string;
}

export interface RuleResult {
  rule: UploadRule;
  failures: RuleFailure[];
}

export interface RulesSummary {
  /** Only rules that failed at least one line, in the order given. */
  results: RuleResult[];
  /** Distinct lines failing at least one Block rule: no download while > 0. */
  blockingLineCount: number;
  /** Distinct lines failing at least one Warn rule. */
  warningLineCount: number;
}

function passes(check: UploadRuleCheck, value: string): boolean {
  if (value === '') return check.type !== 'required';
  switch (check.type) {
    case 'required':
      return true;
    case 'equals':
      return value === check.value.trim();
    case 'oneOf':
      return check.values.some((allowed) => allowed.trim() === value);
    case 'numberBetween':
      return isBetween(Number(value), check.min, check.max);
    case 'maxLength':
      return value.length <= check.max;
  }
}

function isBetween(n: number, min: number | null, max: number | null): boolean {
  if (!Number.isFinite(n)) return false;
  return (min === null || n >= min) && (max === null || n <= max);
}

function evaluateRule(lines: UploadLine[], rule: UploadRule): RuleResult {
  const index = columnIndex(rule.column);
  const failures = lines.flatMap((line, lineIndex) => {
    const value = (line.cells[index] ?? '').trim();
    return passes(rule.check, value)
      ? []
      : [{ line: lineIndex + 1, sheetRow: line.sheetRow, value }];
  });
  return { rule, failures };
}

function distinctLines(
  results: RuleResult[],
  severity: UploadRule['severity'],
): number {
  const rows = new Set<number>();
  for (const result of results) {
    if (result.rule.severity !== severity) continue;
    for (const failure of result.failures) rows.add(failure.sheetRow);
  }
  return rows.size;
}

/** A rule applies to every layout, or only to the one it names. */
function appliesTo(rule: UploadRule, layoutId: string | null): boolean {
  return rule.enabled && (rule.layoutId === null || rule.layoutId === layoutId);
}

export function evaluateUploadRules(
  lines: UploadLine[],
  rules: UploadRule[],
  layoutId: string | null,
): RulesSummary {
  const results: RuleResult[] = [];
  for (const rule of rules) {
    if (!appliesTo(rule, layoutId)) continue;
    const result = evaluateRule(lines, rule);
    if (result.failures.length > 0) results.push(result);
  }
  return {
    results,
    blockingLineCount: distinctLines(results, 'block'),
    warningLineCount: distinctLines(results, 'warn'),
  };
}
